import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Heading from 'components/partials/heading/heading';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import Tabs from 'components/partials/tabs/tabs';
import ActionMenu from 'components/partials/action-menu/action-menu';
import Loader from 'components/partials/loader/loader';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import toast from 'components/partials/toast/toast';

import { useInsightDetails } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/hooks/use-insight-details';
import { useInsightComments } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/hooks/use-insight-comments';
import { useExportPdf } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/hooks/use-export-pdf';
import InsightDetailsTab from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/tabs/insight-details-tab';
import AccessTab from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/tabs/access-tab';
import UserActivityTab from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/tabs/user-activity-tab';
import PdfContent from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/pdf-content';
import { getInsightDetailsActionMenuItems } from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/insight-action-menu-items';

import { useDeleteClientInsight } from 'hooks/use-delete-client-insight';

import { useClient } from 'hooks/api/use-clients';
import { useCurrentUser } from 'hooks/api/use-current-user';

import { formatDate } from 'helpers/dates-formatter';

import Icons from 'assets/icons';

interface TabItem {
  label: string;
  value: string;
}

const getTabItems = (): TabItem[] => [
  { label: 'Details', value: 'details' },
  { label: 'Access', value: 'access' },
  { label: 'User Activity', value: 'user-activity' },
];

const InsightDetails = (): React.JSX.Element => {
  const { clientId, insightId } = useParams<{
    clientId: string;
    insightId: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('details');
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const currentUser = useCurrentUser();
  const currentClient = useClient(clientId);
  const { insight, loading, error } = useInsightDetails({
    clientId,
    insightId,
  });

  const { comments, loading: commentsLoading } = useInsightComments({
    insightId,
  });

  const { exportToPdf, isExporting } = useExportPdf();

  const userId = currentUser?.data?.oktaUserId;

  const { deleteInsight } = useDeleteClientInsight();

  const canDeleteInsight = true;

  if (loading) {
    return <Loader loading={true} />;
  }

  if (error || !insight) {
    return (
      <ResponsiveWrapper customClass="flex flex-col">
        <div className="text-center py-8">
          <p className="text-red-500">Error loading insight details</p>
        </div>
      </ResponsiveWrapper>
    );
  }

  const breadcrumbs = [
    { label: 'Clients', href: '/admin/clients' },
    {
      label: currentClient?.data?.name || 'Client',
      href: `/admin/clients/${clientId}`,
    },
    { label: 'Insights', href: `/admin/clients/${clientId}/insights` },
    { label: insight.name ?? 'Insight', isActive: true },
  ];

  const handleDeleteInsight = async (): Promise<void> => {
    if (!userId || !clientId || !insightId) return;

    try {
      await deleteInsight({
        userId,
        clientId: Number(clientId),
        insightId: Number(insightId),
      });

      toast.success({
        title: 'Insight deleted',
        message: 'The insight has been successfully deleted.',
      });

      navigate(`/admin/clients/${clientId}/insights`);
    } catch (err) {
      console.error('Error deleting insight:', err);

      toast.error({
        title: 'Failed to delete insight',
        message:
          (err as Error)?.message ||
          'An error occurred while deleting the insight.',
      });
    }
  };

  const handleDownloadPdf = async (): Promise<void> => {
    await exportToPdf({
      insightName: insight.name ?? 'Insight',
      contentRef: pdfContentRef,
    });
  };

  const renderTab = (): React.JSX.Element | null => {
    switch (activeTab) {
      case 'details':
        return (
          <InsightDetailsTab
            insight={insight}
            comments={comments}
            commentsLoading={commentsLoading}
          />
        );
      case 'access':
        return <AccessTab insight={insight} clientId={clientId} />;
      case 'user-activity':
        return <UserActivityTab insightId={insightId} />;
      default:
        return null;
    }
  };

  return (
    <ResponsiveWrapper customClass="flex flex-col">
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <div className="flex justify-between items-start mt-6 mb-5 gap-x-8">
        <div>
          <Heading variant="h1" customClass="text-3xl mb-1.5 block">
            {insight.name}
          </Heading>
          <div className="flex items-center text-gray-700 mb-4 gap-x-3">
            <div className="flex items-center gap-1">
              <Icons.Calendar className="size-4" />
              <div className="text-sm font-semibold">
                {formatDate(insight.createdDate, 'MMM D, YYYY')}
              </div>
            </div>
            <div>
              <span className="text-gray-600 text-sm">Created by: </span>
              <span className="text-gray-700 text-sm font-semibold">
                {insight.createdBy || 'Unknown'}
              </span>
            </div>
          </div>
        </div>

        <ActionMenu
          buttonName="Download PDF"
          onEditClick={handleDownloadPdf}
          options={getInsightDetailsActionMenuItems({
            insightName: insight.name,
            onDelete: handleDeleteInsight,
            canDelete: canDeleteInsight,
          })}
          iconBefore={<Icons.Download />}
          isActionMenuDisabled={isExporting}
          loading={isExporting}
        />
      </div>

      <Tabs
        items={getTabItems()}
        activeTab={activeTab}
        onSelect={setActiveTab}
        customClass="mb-4"
        data-testid="insight-details-tabs"
      />
      <div className="relative flex flex-1 flex-col">{renderTab()}</div>

      {/* Hidden PDF content for download */}
      <div
        className="fixed top-[-9999px] left-[-9999px] opacity-0 pointer-events-none"
        aria-hidden="true"
      >
        <PdfContent
          ref={pdfContentRef}
          insight={insight}
          comments={comments}
          showViewReport={false}
        />
      </div>
    </ResponsiveWrapper>
  );
};

export default InsightDetails;
